/**
 * InvitationDetail Page
 * Contractor views invitation details and submits quotes
 */

import { ArrowLeft, Calendar, IndianRupee } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import {
	declineInvitation,
	getInvitationDetails,
	type ProjectContractorItem,
	requestCompletion,
	submitContractorQuote,
} from "@/features/marketplace/services/marketplace-api.service";
import { formatPrice } from "@/lib/format";
import { analytics } from "@/services/analytics.service";
import { QuoteSubmissionForm } from "../components/quote-submission-form";

export function InvitationDetailPage() {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [invitation, setInvitation] = useState<ProjectContractorItem | null>(
		null,
	);
	const [isLoading, setIsLoading] = useState(true);
	const [showQuoteForm, setShowQuoteForm] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [requestingCompletion, setRequestingCompletion] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const loadInvitation = useCallback(async () => {
		if (!id) return;
		setIsLoading(true);
		setError(null);
		try {
			const data = await getInvitationDetails(id);
			setInvitation(data);
			if (data) analytics.contractor.opportunityViewed({ invitationId: id });
		} catch (e) {
			console.error("Failed to load invitation details:", e);
			setError(t("common.loadError", "Failed to load invitation details"));
		} finally {
			setIsLoading(false);
		}
	}, [id, t]);

	useEffect(() => {
		if (id) loadInvitation();
	}, [id, loadInvitation]);

	const handleSubmitQuote = async (data: {
		quoteAmount: number;
		quoteDetails?: string;
	}) => {
		if (!id) return;
		setSubmitting(true);
		try {
			const result = await submitContractorQuote(id, data);
			if (result.success) {
				await loadInvitation();
				setShowQuoteForm(false);
			}
		} finally {
			setSubmitting(false);
		}
	};

	const handleDecline = async () => {
		if (!id) return;
		setSubmitting(true);
		try {
			const result = await declineInvitation(id);
			if (result.success) {
				navigate("/dashboard");
			}
		} finally {
			setSubmitting(false);
		}
	};

	const handleRequestCompletion = async () => {
		if (!id) return;
		setRequestingCompletion(true);
		try {
			const result = await requestCompletion(id);
			if (result.success) {
				toast.success("Completion requested — the homeowner has been notified");
				await loadInvitation();
			} else {
				toast.error(result.error ?? "Failed to request completion");
			}
		} finally {
			setRequestingCompletion(false);
		}
	};

	if (isLoading) {
		return (
			<div className="min-h-screen bg-muted p-4">
				<div className="mx-auto max-w-2xl space-y-4">
					<Skeleton className="h-8 w-48" />
					<Skeleton className="h-48 w-full rounded-lg" />
					<Skeleton className="h-32 w-full rounded-lg" />
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="min-h-screen bg-muted p-4 flex items-center justify-center">
				<Card className="w-full max-w-md rounded-2xl border-0 bg-dashboard-paper shadow-xl">
					<CardContent className="flex flex-col items-center text-center p-6 space-y-4">
						<div className="flex h-12 w-12 items-center justify-center rounded-full bg-dashboard-pill-risk-bg text-dashboard-pill-risk">
							<svg
								className="h-6 w-6"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<title>Error</title>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
								/>
							</svg>
						</div>
						<CardTitle className="text-xl font-bold text-dashboard-text">
							Failed to load invitation
						</CardTitle>
						<CardDescription className="text-sm text-dashboard-muted">
							{error}
						</CardDescription>
						<div className="flex gap-2 w-full">
							<Button
								onClick={loadInvitation}
								className="flex-1 bg-teal-600 hover:bg-teal-700 text-dashboard-on-fill font-medium py-2 rounded-xl"
							>
								Retry
							</Button>
							<Button
								variant="outline"
								onClick={() => navigate("/dashboard")}
								className="flex-1"
							>
								{t("common.backToDashboard")}
							</Button>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (!invitation) {
		return (
			<div className="min-h-screen bg-muted p-4">
				<div className="mx-auto max-w-2xl text-center">
					<p className="text-muted-foreground">
						{t("contractor.invitationNotFound")}
					</p>
					<Button
						variant="outline"
						onClick={() => navigate("/dashboard")}
						className="mt-4"
					>
						{t("common.backToDashboard")}
					</Button>
				</div>
			</div>
		);
	}

	const project = invitation.project;

	return (
		<div className="min-h-screen bg-muted">
			<header className="sticky top-[var(--app-header-h,0px)] z-50 border-b border-border bg-card">
				<div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-4">
					<button type="button" onClick={() => navigate(-1)}>
						<ArrowLeft className="h-5 w-5 text-foreground" />
					</button>
					<h1 className="text-lg font-semibold text-foreground">
						{t("contractor.invitationDetails")}
					</h1>
				</div>
			</header>

			<main className="mx-auto max-w-2xl space-y-4 px-4 py-6">
				{/* Invitation Status */}
				<Card>
					<CardContent className="p-6">
						<div className="flex items-center justify-between">
							<h2 className="text-lg font-semibold text-foreground">
								{t("contractor.status")}
							</h2>
							<StatusBadge status={invitation.status} />
						</div>
						<p className="mt-1 text-sm text-muted-foreground">
							{t("common.invitedOn", {
								date: new Date(invitation.createdAt).toLocaleDateString(
									"en-IN",
								),
							})}
						</p>
						{invitation.quoteAmount != null && (
							<p className="mt-2 text-sm text-foreground">
								{t("contractor.yourQuote", {
									amount: formatPrice(invitation.quoteAmount),
								})}
							</p>
						)}
						{invitation.hiredAt && (
							<p className="mt-1 text-sm text-dashboard-success">
								{t("common.hiredOn", {
									date: new Date(invitation.hiredAt).toLocaleDateString(
										"en-IN",
									),
								})}
							</p>
						)}
					</CardContent>
				</Card>

				{/* Project Info */}
				{project && (
					<Card>
						<CardContent className="p-6">
							<h2 className="text-lg font-semibold text-foreground">
								{project.title}
							</h2>
							<StatusBadge status={project.status} className="mt-2" />
							<div className="mt-4 space-y-2">
								<p className="flex items-center gap-2 text-sm text-muted-foreground">
									<span className="font-medium">{t("project.scope")}:</span>{" "}
									{project.scope === "full_home"
										? t("common.fullHome")
										: t("common.partial")}
								</p>
								{project.budgetMin != null && project.budgetMax != null && (
									<p className="flex items-center gap-2 text-sm text-muted-foreground">
										<IndianRupee className="h-3.5 w-3.5" />
										{formatPrice(project.budgetMin)} -{" "}
										{formatPrice(project.budgetMax)}
									</p>
								)}
								{project.timelineWeeks && (
									<p className="flex items-center gap-2 text-sm text-muted-foreground">
										<Calendar className="h-3.5 w-3.5" />
										{project.timelineWeeks} {t("common.weeks")}
									</p>
								)}
							</div>
						</CardContent>
					</Card>
				)}

				{/* Actions */}
				{invitation.status === "invited" && (
					<Card>
						<CardContent className="p-6">
							{showQuoteForm ? (
								<QuoteSubmissionForm
									onSubmit={handleSubmitQuote}
									onCancel={() => setShowQuoteForm(false)}
									isLoading={submitting}
								/>
							) : (
								<div className="space-y-3">
									<h3 className="font-semibold text-foreground">
										{t("contractor.respondToInvitation")}
									</h3>
									<div className="flex gap-3">
										<Button
											variant="primary"
											onClick={() => setShowQuoteForm(true)}
										>
											{t("contractor.submitQuote")}
										</Button>
										<Button
											variant="outline"
											onClick={handleDecline}
											disabled={submitting}
										>
											{submitting
												? t("contractor.declining")
												: t("contractor.decline")}
										</Button>
									</div>
								</div>
							)}
						</CardContent>
					</Card>
				)}

				{invitation.status === "hired" && (
					<Card>
						<CardContent className="p-6 space-y-3">
							<h3 className="font-semibold text-foreground">
								{t("contractor.yourAssignment")}
							</h3>
							<p className="text-sm text-muted-foreground">
								{t(
									"contractor.requestCompletionDesc",
									"Once your work is done, request the homeowner to confirm completion.",
								)}
							</p>
							<Button
								variant="primary"
								onClick={handleRequestCompletion}
								disabled={requestingCompletion}
							>
								{requestingCompletion
									? t("common.requesting", "Requesting…")
									: t("contractor.requestCompletion", "Request Completion")}
							</Button>
						</CardContent>
					</Card>
				)}
			</main>
		</div>
	);
}
